// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** API
import useJwt from '@src/auth/jwt/useJwt'

// ** LocalStorage key for persistence
const STORAGE_KEY = 'clientOnboardingProgress'
const STORAGE_EXPIRY_DAYS = 30

// ** Helper: load from localStorage
const loadFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    const parsed = JSON.parse(stored)
    if (parsed.expiresAt && new Date().getTime() > parsed.expiresAt) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

// ** Helper: save to localStorage
const saveToLocalStorage = (state) => {
  try {
    const expiresAt = new Date().getTime() + (STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      currentStep: state.currentStep,
      formData: state.formData,
      lastSaved: new Date().toISOString(),
      expiresAt
    }))
  } catch (e) {
    console.error('Error saving client onboarding progress:', e)
  }
}

// ─── Async Thunks ────────────────────────────────────────────────

// Comments
export const fetchStepComments = createAsyncThunk(
  'clientOnboarding/fetchStepComments',
  async ({ onboardingId, step }, { rejectWithValue }) => {
    try {
      const res = await useJwt.getClientOnboardingComments(onboardingId, step)
      return { step, comments: res.data?.data || [] }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch comments')
    }
  }
)

export const addStepComment = createAsyncThunk(
  'clientOnboarding/addStepComment',
  async ({ onboardingId, step, comment, isInternal }, { rejectWithValue }) => {
    try {
      const res = await useJwt.addClientOnboardingComment(onboardingId, {
        step: step,
        comment,
        is_internal: isInternal ? 1 : 0
      })
      return { step, comment: res.data?.data }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add comment')
    }
  }
)

// Attachments
export const fetchStepAttachments = createAsyncThunk(
  'clientOnboarding/fetchStepAttachments',
  async ({ onboardingId, step }, { rejectWithValue }) => {
    try {
      const res = await useJwt.getClientOnboardingAttachments(onboardingId, step)
      return { step, attachments: res.data?.data || [] }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch attachments')
    }
  }
)

export const uploadStepAttachment = createAsyncThunk(
  'clientOnboarding/uploadStepAttachment',
  async ({ onboardingId, step, files }, { rejectWithValue }) => {
    try {
      const formData = new FormData()
      formData.append('step', step)
      files.forEach((file, i) => formData.append(`files[${i}]`, file))
      const res = await useJwt.uploadClientOnboardingAttachment(onboardingId, formData)
      return { step, attachments: res.data?.data || [] }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to upload attachment')
    }
  }
)

export const deleteStepAttachment = createAsyncThunk(
  'clientOnboarding/deleteStepAttachment',
  async ({ onboardingId, attachmentId, step }, { rejectWithValue }) => {
    try {
      await useJwt.deleteClientOnboardingAttachment(onboardingId, attachmentId)
      return { step, attachmentId }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete attachment')
    }
  }
)

// Notifications
export const sendStepNotification = createAsyncThunk(
  'clientOnboarding/sendStepNotification',
  async ({ onboardingId, step, toTeam, toUserId, message }, { rejectWithValue }) => {
    try {
      const res = await useJwt.sendClientOnboardingNotification(onboardingId, {
        step_number: step,
        to_team: toTeam,
        to_user_id: toUserId,
        message
      })
      return res.data?.data
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to send notification')
    }
  }
)

export const fetchNotifications = createAsyncThunk(
  'clientOnboarding/fetchNotifications',
  async ({ onboardingId }, { rejectWithValue }) => {
    try {
      const res = await useJwt.getClientOnboardingNotifications(onboardingId)
      return res.data?.data || []
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch notifications')
    }
  }
)

// Acknowledgment
export const acknowledgeStep = createAsyncThunk(
  'clientOnboarding/acknowledgeStep',
  async ({ onboardingId, step }, { rejectWithValue }) => {
    try {
      const res = await useJwt.acknowledgeClientOnboardingStep(onboardingId, { step_number: step })
      return { step, data: res.data?.data }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to acknowledge step')
    }
  }
)

// Reject step
export const rejectStep = createAsyncThunk(
  'clientOnboarding/rejectStep',
  async ({ onboardingId, step, reason }, { rejectWithValue }) => {
    try {
      const res = await useJwt.rejectClientOnboardingStep(onboardingId, {
        step_number: step,
        reason
      })
      return { step, data: res.data?.data }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to reject step')
    }
  }
)

// Email logs
export const fetchEmailLogs = createAsyncThunk(
  'clientOnboarding/fetchEmailLogs',
  async ({ onboardingId }, { rejectWithValue }) => {
    try {
      const res = await useJwt.getClientOnboardingEmailLogs(onboardingId)
      return res.data?.data || []
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch email logs')
    }
  }
)

export const addEmailLog = createAsyncThunk(
  'clientOnboarding/addEmailLog',
  async ({ onboardingId, data }, { rejectWithValue }) => {
    try {
      const res = await useJwt.addClientOnboardingEmailLog(onboardingId, data)
      return res.data?.data
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add email log')
    }
  }
)

// Timers
export const startTimer = createAsyncThunk(
  'clientOnboarding/startTimer',
  async ({ onboardingId, eventType }, { rejectWithValue }) => {
    try {
      const res = await useJwt.startClientOnboardingTimer(onboardingId, { event_type: eventType })
      return { eventType, data: res.data?.data }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to start timer')
    }
  }
)

export const stopTimer = createAsyncThunk(
  'clientOnboarding/stopTimer',
  async ({ onboardingId, eventType }, { rejectWithValue }) => {
    try {
      const res = await useJwt.stopClientOnboardingTimer(onboardingId, { event_type: eventType })
      return { eventType, data: res.data?.data }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to stop timer')
    }
  }
)

// Messages (internal chat)
export const fetchMessages = createAsyncThunk(
  'clientOnboarding/fetchMessages',
  async ({ onboardingId }, { rejectWithValue }) => {
    try {
      const res = await useJwt.getClientOnboardingMessages(onboardingId)
      return res.data?.data || []
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch messages')
    }
  }
)

export const sendMessage = createAsyncThunk(
  'clientOnboarding/sendMessage',
  async ({ onboardingId, message, mentionedTeams }, { rejectWithValue }) => {
    try {
      const res = await useJwt.sendClientOnboardingMessage(onboardingId, {
        message,
        mentioned_teams: mentionedTeams
      })
      return res.data?.data
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to send message')
    }
  }
)

// Activity timeline
export const fetchActivityTimeline = createAsyncThunk(
  'clientOnboarding/fetchActivityTimeline',
  async ({ onboardingId }, { rejectWithValue }) => {
    try {
      const res = await useJwt.getClientOnboardingTimeline(onboardingId)
      return res.data?.data || []
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch timeline')
    }
  }
)

// Delete onboarding record
export const deleteOnboarding = createAsyncThunk(
  'clientOnboarding/deleteOnboarding',
  async ({ onboardingId }, { rejectWithValue }) => {
    try {
      await useJwt.deleteClientOnboarding(onboardingId)
      return onboardingId
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete record')
    }
  }
)

// ─── Initial State ───────────────────────────────────────────────

const getInitialState = () => {
  const saved = loadFromLocalStorage()
  return {
    currentStep: saved?.currentStep || 1,
    formData: saved?.formData || {},
    lastSaved: saved?.lastSaved || null,
    hasProgress: !!saved,

    // Per-step data
    comments: {},       // { [step]: [] }
    attachments: {},    // { [step]: [] }
    stepStatuses: {},   // { [step]: 'pending' | 'notified' | 'verified' | 'submitted' | 'rejected' }
    acknowledgments: {},// { [step]: { user, timestamp } }

    // Global data
    notifications: [],
    emailLogs: [],
    messages: [],
    timers: {},         // { process_start: {...}, payer_list: {...} }
    activityTimeline: [],

    // Loading states
    loading: {
      comments: false,
      attachments: false,
      notifications: false,
      emailLogs: false,
      messages: false,
      timeline: false
    }
  }
}

// ─── Slice ───────────────────────────────────────────────────────

export const clientOnboardingSlice = createSlice({
  name: 'clientOnboarding',
  initialState: getInitialState(),
  reducers: {
    saveProgress: (state, action) => {
      const { currentStep, formData } = action.payload
      if (currentStep !== undefined) state.currentStep = currentStep
      if (formData !== undefined) state.formData = { ...state.formData, ...formData }
      state.hasProgress = true
      state.lastSaved = new Date().toISOString()
      saveToLocalStorage(state)
    },

    setCurrentStep: (state, action) => {
      state.currentStep = action.payload
      state.lastSaved = new Date().toISOString()
      saveToLocalStorage(state)
    },

    updateStepData: (state, action) => {
      const { step, data } = action.payload
      state.formData[step] = { ...state.formData[step], ...data }
      state.lastSaved = new Date().toISOString()
      saveToLocalStorage(state)
    },

    setStepStatus: (state, action) => {
      const { step, status } = action.payload
      state.stepStatuses[step] = status
    },

    clearProgress: (state) => {
      Object.assign(state, getInitialState())
      localStorage.removeItem(STORAGE_KEY)
    },

    resetOnboarding: (state) => {
      Object.assign(state, {
        currentStep: 1,
        formData: {},
        hasProgress: false,
        lastSaved: null,
        comments: {},
        attachments: {},
        stepStatuses: {},
        acknowledgments: {},
        notifications: [],
        emailLogs: [],
        messages: [],
        timers: {},
        activityTimeline: []
      })
      localStorage.removeItem(STORAGE_KEY)
    }
  },

  extraReducers: (builder) => {
    // Comments
    builder
      .addCase(fetchStepComments.pending, (state) => { state.loading.comments = true })
      .addCase(fetchStepComments.fulfilled, (state, { payload }) => {
        state.comments[payload.step] = payload.comments
        state.loading.comments = false
      })
      .addCase(fetchStepComments.rejected, (state) => { state.loading.comments = false })
      .addCase(addStepComment.fulfilled, (state, { payload }) => {
        if (!state.comments[payload.step]) state.comments[payload.step] = []
        state.comments[payload.step].push(payload.comment)
      })

    // Attachments
    builder
      .addCase(fetchStepAttachments.pending, (state) => { state.loading.attachments = true })
      .addCase(fetchStepAttachments.fulfilled, (state, { payload }) => {
        state.attachments[payload.step] = payload.attachments
        state.loading.attachments = false
      })
      .addCase(fetchStepAttachments.rejected, (state) => { state.loading.attachments = false })
      .addCase(uploadStepAttachment.fulfilled, (state, { payload }) => {
        state.attachments[payload.step] = payload.attachments
      })
      .addCase(deleteStepAttachment.fulfilled, (state, { payload }) => {
        if (state.attachments[payload.step]) {
          state.attachments[payload.step] = state.attachments[payload.step]
            .filter(a => a.id !== payload.attachmentId)
        }
      })

    // Notifications
    builder
      .addCase(fetchNotifications.pending, (state) => { state.loading.notifications = true })
      .addCase(fetchNotifications.fulfilled, (state, { payload }) => {
        state.notifications = payload
        state.loading.notifications = false
      })
      .addCase(fetchNotifications.rejected, (state) => { state.loading.notifications = false })
      .addCase(sendStepNotification.fulfilled, (state, { payload }) => {
        if (payload) state.notifications.push(payload)
      })

    // Acknowledgments
    builder
      .addCase(acknowledgeStep.fulfilled, (state, { payload }) => {
        state.acknowledgments[payload.step] = payload.data
      })

    // Rejections
    builder
      .addCase(rejectStep.fulfilled, (state, { payload }) => {
        state.stepStatuses[payload.step] = 'rejected'
      })

    // Email logs
    builder
      .addCase(fetchEmailLogs.pending, (state) => { state.loading.emailLogs = true })
      .addCase(fetchEmailLogs.fulfilled, (state, { payload }) => {
        state.emailLogs = payload
        state.loading.emailLogs = false
      })
      .addCase(fetchEmailLogs.rejected, (state) => { state.loading.emailLogs = false })
      .addCase(addEmailLog.fulfilled, (state, { payload }) => {
        if (payload) state.emailLogs.push(payload)
      })

    // Timers
    builder
      .addCase(startTimer.fulfilled, (state, { payload }) => {
        state.timers[payload.eventType] = payload.data
      })
      .addCase(stopTimer.fulfilled, (state, { payload }) => {
        state.timers[payload.eventType] = payload.data
      })

    // Messages
    builder
      .addCase(fetchMessages.pending, (state) => { state.loading.messages = true })
      .addCase(fetchMessages.fulfilled, (state, { payload }) => {
        state.messages = payload
        state.loading.messages = false
      })
      .addCase(fetchMessages.rejected, (state) => { state.loading.messages = false })
      .addCase(sendMessage.fulfilled, (state, { payload }) => {
        if (payload) state.messages.push(payload)
      })

    // Activity timeline
    builder
      .addCase(fetchActivityTimeline.pending, (state) => { state.loading.timeline = true })
      .addCase(fetchActivityTimeline.fulfilled, (state, { payload }) => {
        state.activityTimeline = payload
        state.loading.timeline = false
      })
      .addCase(fetchActivityTimeline.rejected, (state) => { state.loading.timeline = false })
  }
})

export const {
  saveProgress,
  setCurrentStep,
  updateStepData,
  setStepStatus,
  clearProgress,
  resetOnboarding
} = clientOnboardingSlice.actions

export default clientOnboardingSlice.reducer
